import { Injectable, Inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CanonicalService {
  private renderer: Renderer2;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * Sets canonical URL (SSR safe)
   * Automatically normalizes:
   * - HTTPS
   * - Base domain
   * - Removes query params
   * - Removes hash
   * - Removes trailing slashes (except root)
   * - Converts to lowercase
   */
  setCanonicalURL(url?: string): void {
    try {
      const baseUrl = environment.websiteURL.replace(/\/$/, '');

      let finalUrl = baseUrl;

      if (url && typeof url === 'string') {
        // Remove query params and hash for canonical
        let cleanPath = url.split('?')[0].split('#')[0];

        // Remove trailing slash (unless it's root)
        if (cleanPath !== '/' && cleanPath.endsWith('/')) {
          cleanPath = cleanPath.slice(0, -1);
        }

        // Normalize to lowercase for consistency
        cleanPath = cleanPath.toLowerCase();

        // Ensure single slash
        const normalizedPath = cleanPath.startsWith('/')
          ? cleanPath
          : `/${cleanPath}`;

        finalUrl = `${baseUrl}${normalizedPath}`;
      }

      this.updateCanonicalTag(finalUrl);
    } catch (error) {
      console.error('Error setting canonical URL:', error);
    }
  }

  /**
   * Creates or updates canonical link tag
   */
  private updateCanonicalTag(url: string): void {
    if (!this.document || !this.document.head) return;

    try {
      // Remove existing canonical links
      const existingLinks = this.document.querySelectorAll(
        "link[rel='canonical']"
      );

      existingLinks.forEach((link) => {
        this.renderer.removeChild(this.document.head, link);
      });

      // Create new link element
      const link: HTMLLinkElement =
        this.renderer.createElement('link');

      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.setAttribute(link, 'href', url);

      this.renderer.appendChild(this.document.head, link);
    } catch (error) {
      console.error('Error updating canonical tag:', error);
    }
  }

  /**
   * Returns current canonical URL
   */
  getCanonicalURL(): string | null {
    if (!this.document) return null;

    const link = this.document.querySelector(
      "link[rel='canonical']"
    ) as HTMLLinkElement | null;

    return link ? link.href : null;
  }

  /**
   * Removes canonical tag
   */
  removeCanonicalURL(): void {
    if (!this.document || !this.document.head) return;

    const links = this.document.querySelectorAll(
      "link[rel='canonical']"
    );

    links.forEach((link) => {
      this.renderer.removeChild(this.document.head, link);
    });
  }
}